/**
 * Participant Validation Utilities
 *
 * Ensures participant data meets backend validation requirements before submission.
 * Backend requires strict schema validation with:
 * - firstName: non-empty string (required)
 * - consentToName: explicit boolean true/false (required)
 * - relationshipRole: optional string
 * - NO unknown fields allowed
 */

import { Participant } from '@/types/category';

export interface ParticipantValidationResult {
  isValid: boolean;
  errors: string[];
  fixedParticipants?: Participant[];
}

/**
 * Validates participant data for backend submission
 *
 * Backend validation rules (BREAKING CHANGES):
 * 1. consentToName MUST be explicit true or false (cannot be missing or null)
 * 2. firstName MUST be non-empty string
 * 3. Minimum 2 consenting participants required for group sessions
 * 4. Unknown fields are rejected
 */
export function validateParticipantsForBackend(
  participants: Participant[]
): ParticipantValidationResult {
  const errors: string[] = [];
  const fixedParticipants: Participant[] = [];

  if (!participants || !Array.isArray(participants)) {
    return {
      isValid: false,
      errors: ['Participants must be an array']
    };
  }

  if (participants.length === 0) {
    return {
      isValid: false,
      errors: ['At least one participant is required']
    };
  }

  // Validate each participant
  participants.forEach((p, index) => {
    const participantErrors: string[] = [];

    // Check firstName
    if (!p.firstName || typeof p.firstName !== 'string' || !p.firstName.trim()) {
      participantErrors.push(`Participant ${index + 1}: firstName is required and cannot be empty`);
    }

    // Check consentToName (BREAKING CHANGE: must be explicit boolean)
    if (p.consentToName === undefined || p.consentToName === null) {
      participantErrors.push(
        `Participant ${index + 1} (${p.firstName || 'unnamed'}): consentToName must be explicitly true or false (cannot be missing or null)`
      );
    } else if (typeof p.consentToName !== 'boolean') {
      participantErrors.push(
        `Participant ${index + 1} (${p.firstName || 'unnamed'}): consentToName must be a boolean, got ${typeof p.consentToName}`
      );
    }

    if (participantErrors.length > 0) {
      errors.push(...participantErrors);
    } else {
      // Only include backend-compatible fields
      fixedParticipants.push({
        firstName: p.firstName.trim(),
        consentToName: p.consentToName,
        relationshipRole: p.relationshipRole || undefined
      });
    }
  });

  // Check minimum consenting participants (BREAKING CHANGE: min 2 required)
  const consentingCount = fixedParticipants.filter(p => p.consentToName === true).length;
  if (consentingCount < 2) {
    errors.push(
      `Minimum 2 consenting participants required. Found ${consentingCount} with consent. ` +
      `Please ensure at least 2 participants have "consentToName: true"`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    fixedParticipants: errors.length === 0 ? fixedParticipants : undefined
  };
}

/**
 * Ensures a participant object has the required consentToName field
 * with a valid boolean value. Defaults to false if missing.
 */
export function ensureConsentField(participant: Partial<Participant>): Participant {
  return {
    firstName: participant.firstName || '',
    consentToName: typeof participant.consentToName === 'boolean'
      ? participant.consentToName
      : false, // Default to false if missing (explicit choice required)
    relationshipRole: participant.relationshipRole,
    // Only include other optional fields if they exist
    ...(participant.lastName && { lastName: participant.lastName }),
    ...(participant.preferredName && { preferredName: participant.preferredName }),
    ...(participant.pronouns && { pronouns: participant.pronouns }),
    ...(participant.subgroup && { subgroup: participant.subgroup }),
    ...(participant.roleTitle && { roleTitle: participant.roleTitle }),
    ...(participant.likes && { likes: participant.likes }),
    ...(participant.locationTag && { locationTag: participant.locationTag }),
    ...(participant.hopeForSession && { hopeForSession: participant.hopeForSession }),
    ...(participant.personalChallenge && { personalChallenge: participant.personalChallenge }),
    ...(participant.meditationExperience && { meditationExperience: participant.meditationExperience }),
    ...(participant.connectionStyle && { connectionStyle: participant.connectionStyle }),
    ...(participant.backgroundStory && { backgroundStory: participant.backgroundStory }),
    ...(participant.biography && { biography: participant.biography })
  } as Participant;
}

/**
 * Prepares participant list for backend submission by:
 * 1. Stripping unknown fields (backend rejects them)
 * 2. Ensuring required fields are present and valid
 * 3. Trimming firstName whitespace
 */
export function prepareParticipantsForBackend(participants: Participant[]): Participant[] {
  return participants.map(p => ({
    firstName: (p.firstName || '').trim(),
    consentToName: p.consentToName === true, // Explicit boolean
    relationshipRole: p.relationshipRole || undefined
  }));
}

/**
 * Parses backend validation error messages and returns user-friendly error text
 */
export function parseBackendValidationError(errorMessage: string): string {
  // Check for common backend validation error patterns
  if (errorMessage.toLowerCase().includes('consenttoname')) {
    return 'All participants must have explicit consent status. Please ensure each participant has "Include name in session" checked or unchecked.';
  }

  if (errorMessage.toLowerCase().includes('minimum 2 consenting participants')) {
    return 'At least 2 participants must consent to having their names used in the session. Please check "Include name in session" for at least 2 participants.';
  }

  if (errorMessage.toLowerCase().includes('firstname cannot be empty')) {
    return 'All participants must have a name. Please fill in the name field for all participants.';
  }

  if (errorMessage.toLowerCase().includes('participant validation failed')) {
    return errorMessage; // Return full detailed message for debugging
  }

  // Default: return original message
  return errorMessage;
}
